import {
  AdjacentType,
  ComparisonType,
  DatastoreObjectType,
  DatastoreObjectTypes,
  EdgeParams,
  ErrData,
  ErrDataPromise,
  FilterGroup,
  LogicalOperatorType,
  Traversal,
  ValidBusinessObject,
  ValidBusinessObjectList,
} from '@iotv/iotv-v3-types'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../util/lambda/debug'
import { getAllParentEdgeAttributeDefinitions, getChildEdgeTypeId, getParentEdgeTypeId } from '../classDependentTypes/EdgeTypes/edgeTypeMatrixFns'
import { ModelClassNames } from '../classDependentTypes/ModelClassNames'
import { BranchingQueryDefinition, IBranchingQueryDefinition } from '../util/dynamodb/GraphQuery/BranchingQueryDefinition'
import { edgeTypeMatrix } from '../classDependentTypes/EdgeTypes/EdgeTypeMatrix'
import { GetBranchingQueryDefinitionInput } from './types'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

/** special key used to tag final query path so that additional queries can be added */
const GROUP_SUPPORTED_TYPE_QUERY_KEY = 'groupToSupportedTypeQuery'

/** limit to nodes with correct supportedType */
const getGroupSupportedTypeFilter = (supportedType: string): FilterGroup[] => [
  {
    setOperation: LogicalOperatorType.AND,
    filters: [
      {
        key: 'supportedType',
        predicate: ComparisonType.EQUALS,
        value: supportedType,
      },
    ],
  },
]

/** limit to sub groups with correct supported type */
const getGroupSubGroupParams = (supportedType: string): EdgeParams[] => [
  {
    objectTypeId: 'Group',
    adjacencyType: AdjacentType.CHILD,
    edgeTypeId: edgeTypeMatrix.Group.Group.group_has_subGroup.edgeTypeId,
    nodeFilters: getGroupSupportedTypeFilter(supportedType),
  },
]

export type GetItemTypeToGroupNestedQueryDefInput = {
  /** the type of object that 'has group' and 'is in group' */
  groupDefiningObjectTypeId: 'Customer' | 'Property'

  /** the item type to limit groups to and retrieve from
   * TODO replace string with type constructed by union of all DeviceEnabledType objectTypeIds
   */
  supportedType: 'User' | 'Customer' | 'Device' | 'Gateway' | string

  /** whether to return the contained items in Supported Type groups, because care needs to be taken as very large results sets may be produced */
  includeSupportedTypeInstances: boolean

  /** optional additional queries that will be executed on the final supported type query for each group -> supportedType branch */
  supportedTypeSubQueries?: BranchingQueryDefinition[]
  /** whether to add a  prefix to query ids, to avoid duplication when used in composite functions, default true */
  addIdPrefixes?: boolean
}

/** returns a query definition which will recursively navigate down the group structure from defining type to supported type, returning all groups and, optionally, the instances inside the group.
 * WARN: does not return supported types or groups of supported types that belong to children of defining type in group of defining type under the context object. This is by design to prevent loading too much. The proper solution is changing context customer
 * E.g. Customer -> Device will return all devices under Customer, but not devices belonging to child customers under Customer.
 *
 * @param groupDefiningObjectTypeId - Type that owns the group
 * @param supportedType - type of object sought
 * @param supportedTypeSubQueries - queries to run on each supported type (if they are returned)
 * @param includeSupportedTypeInstances - Option to include instances of supported type from the groups
 * @returns A NodeEdgeMapper containing source item and all supported type groups below leading to supportedTypes (+those supportedType items if includeSupportedTypeInstances is true) + all groups and items of definingTypes below source item
 */
export const getItemTypeToGroupNestedDescendingQueryDef = ({
  groupDefiningObjectTypeId,
  supportedType,
  supportedTypeSubQueries,
  includeSupportedTypeInstances,
  addIdPrefixes = true,
}: GetItemTypeToGroupNestedQueryDefInput): BranchingQueryDefinition => {
  const idPrefix: string = addIdPrefixes ? `${groupDefiningObjectTypeId}-${supportedType}-${Traversal.DESCENDING}-` : ''

  const getGroupToSubGroupQuery = (supportedType: string) =>
    new BranchingQueryDefinition({
      id: `${idPrefix}${supportedType}_groupToGroupQuery`,
      path: getGroupSubGroupParams(supportedType),
      subQueries: [],
    })

  /** ensures original items are returned in results */
  const apexQuery = new BranchingQueryDefinition({
    id: `${idPrefix}apexQuery`,
    path: [
      {
        adjacencyType: AdjacentType.SUBJECT,
        objectTypeId: groupDefiningObjectTypeId,
      },
    ],
    subQueries: [],
  })

  /** eg Customer to Device Group */
  const definingTypeToSupportedTypeGroup: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}definingTypeToSupportedTypeGroup`,
    path: [
      {
        objectTypeId: 'Group',
        adjacencyType: AdjacentType.CHILD,
        edgeTypeId: getChildEdgeTypeId(groupDefiningObjectTypeId, 'Group'),
        nodeFilters: getGroupSupportedTypeFilter(supportedType),
      },
    ],
    subQueries: [],
  })

  /** eg Customer to Customer Group */
  const definingTypeToDefiningTypeGroup: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}definingTypeToDefiningTypeGroup`,
    path: [
      {
        objectTypeId: 'Group',
        adjacencyType: AdjacentType.CHILD,
        edgeTypeId: getChildEdgeTypeId(groupDefiningObjectTypeId, 'Group'),
        nodeFilters: getGroupSupportedTypeFilter(groupDefiningObjectTypeId),
      },
    ],
    subQueries: [],
  })

  /** eg Customer Group to Customer */
  const definingTypeGroupToDefiningType: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}definingTypeGroupToDefiningType`,
    path: [
      {
        objectTypeId: groupDefiningObjectTypeId,
        adjacencyType: AdjacentType.CHILD,
        edgeTypeId: getChildEdgeTypeId('Group', groupDefiningObjectTypeId),
      },
    ],
    subQueries: [],
  })

  /** eg UserGroup allows Device Group */
  const groupAllowsGroup: BranchingQueryDefinition = new BranchingQueryDefinition({
    id: `${idPrefix}groupAllowsGroup`,
    path: [
      {
        objectTypeId: 'Group',
        adjacencyType: AdjacentType.CHILD,
        edgeTypeId: edgeTypeMatrix.Group.Group.group_allows_group.edgeTypeId,
      },
    ],
    subQueries: [],
  })

  /** eg Customer Group to Customer Group */
  const definingTypeSubGroups = getGroupToSubGroupQuery(groupDefiningObjectTypeId)

  /** eg Device Group to Device Group */
  const supportedTypeSubGroups = getGroupToSubGroupQuery(supportedType)

  /** eg Device Group to Device  */
  // Only added if relevant param is true
  const supportedTypeGroupToSupportedType = new BranchingQueryDefinition({
    path: [
      {
        objectTypeId: supportedType,
        adjacencyType: AdjacentType.CHILD,
        edgeTypeId: getChildEdgeTypeId('Group', supportedType),
      },
    ],
    subQueries: [...(supportedTypeSubQueries ?? [])],
    id: `${idPrefix}${GROUP_SUPPORTED_TYPE_QUERY_KEY}`,
  })

  /** eg: Customer to Customer Groups */
  apexQuery.addSubQuery(definingTypeToDefiningTypeGroup)

  /** eg: Customer to Device Groups */
  apexQuery.addSubQuery(definingTypeToSupportedTypeGroup)

  /** eg Customer Groups to sub Customer Groups and Customers */
  definingTypeToDefiningTypeGroup.addSubQuery(definingTypeSubGroups)
  definingTypeToDefiningTypeGroup.addSubQuery(definingTypeGroupToDefiningType)
  definingTypeToDefiningTypeGroup.addSubQuery(groupAllowsGroup)

  /** eg Customer sub groups to further nested sub groups (recursive), and their contained Customers */
  definingTypeSubGroups.addSubQuery(definingTypeSubGroups)
  definingTypeSubGroups.addSubQuery(groupAllowsGroup)
  definingTypeSubGroups.addSubQuery(definingTypeGroupToDefiningType)

  /** eg Customers to Customer Groups ( same as apex ) */
  definingTypeGroupToDefiningType.addSubQuery(definingTypeToDefiningTypeGroup)

  /** eg  Device Groups to sub Device Groups */
  definingTypeToSupportedTypeGroup.addSubQuery(supportedTypeSubGroups)
  definingTypeToSupportedTypeGroup.addSubQuery(groupAllowsGroup)

  /** eg Device sub groups to further nested sub groups (recursive) */
  supportedTypeSubGroups.addSubQuery(supportedTypeSubGroups)
  supportedTypeSubGroups.addSubQuery(groupAllowsGroup)

  if (includeSupportedTypeInstances) {
    /** eg  Device Groups to their contained Devices  */
    definingTypeToSupportedTypeGroup.addSubQuery(supportedTypeGroupToSupportedType)

    /** eg Device sub groups to their contained Devices */
    supportedTypeSubGroups.addSubQuery(supportedTypeGroupToSupportedType)
  }
  return apexQuery
}

export const getDescendingGroupRecuriveQuery = ({
  items,
  groupDefiningObjectTypeId,
  supportedType,
  supportedTypeSubQueries,
  includeSupportedTypeInstances,
}: GetItemTypeToGroupNestedQueryDefInput & {
  items: ValidBusinessObjectList
}): GetBranchingQueryDefinitionInput => {
  const query: GetBranchingQueryDefinitionInput = {
    topLevelQuery: getItemTypeToGroupNestedDescendingQueryDef({
      groupDefiningObjectTypeId,
      supportedType,
      supportedTypeSubQueries,
      includeSupportedTypeInstances,
    }),
    contextObjects: items,
  }

  return query
}
